import type { PortableTextMarkComponentProps } from '@portabletext/react';
import type { ImageWithMetadata} from '../components/SanityImage';
import { imageWithMetadataGroq } from '../components/SanityImage';
import DefinitionPopover from '../popovers/DefinitionPopover';
import GrapePopover from '../popovers/GrapePopover';
import WineStylePopover from '../popovers/WineStylePopover';

const definitionPopoverGroq = /* groq */ `{
    _type,
    'slug': slug.current,
    name,
    'summary': coalesce(summary, mainEntry->summary),
    'hasText': count(description),
    mainEntry -> {
        'slug': slug.current,
        name,
        'hasText': count(description),
    },
    'listPage': *[_type == 'listPage' && listType == 'definition'][0] { _id, title },
}`;

export interface DefinitionPopupSchema {
    _type: 'definition';
    slug?: string;
    name?: string;
    summary?: string;
    hasText: boolean | null;
    mainEntry?: {
        slug?: string;
        name?: string;
        hasText: boolean | null;
    };
    listPage: { _id: string; title: string; path: string };
}

const grapePopoverGroq = /* groq */ `{
    _type,
    'slug': slug.current,
    name,
    'summary': coalesce(summary, mainEntry->summary),
    grapeType,
    'hasText': count(description),
    mainEntry -> {
        'slug': slug.current,
        name,
        'hasText': count(description),
    },
    'listPage': *[_type == 'listPage' && listType == 'grape'][0] { _id, title },
}`;

interface GrapePopupSchema {
    _type: 'grape';
    slug?: string;
    name?: string;
    summary?: string;
    grapeType: 'green' | 'blue';
    hasText: boolean | null;
    mainEntry: {
        slug?: string;
        name?: string;
        hasText: boolean | null;
    };
    listPage: { _id: string; title: string; path: string };
}

const wineStylePopoverGroq = /* groq */ `
    {
        _type,
        'slug': slug.current,
        page,
        title,
        summary,
        image ${imageWithMetadataGroq}
    }
`;

interface WineStylePopoverProps {
    _type: 'wineStyle';
    page?: {
        path: string;
    };
    slug?: string;
    title?: string;
    summary?: string;
    image?: ImageWithMetadata;
}

export const popoverMarkGroq = /* groq */ `
{
    _type,
    _key,
    reference -> {
        _type == 'definition' => ${definitionPopoverGroq},
        _type == 'grape' => ${grapePopoverGroq},
        _type == 'wineStyle' => ${wineStylePopoverGroq}
    },
    'page': *[_type == 'listPage' && listType == ^.reference->_type][0]{
        '_ref': _id,
        _type
    }
}
`;

export interface PopoverProps extends PopupProps {
    _type: 'popoverMark';
}

export function PopoverMark({ value, children }: PortableTextMarkComponentProps<PopoverProps>) {
    if (!value?.reference) return children;

    const { reference } = value;
    switch (reference?._type) {
        case 'definition':
            return <DefinitionPopover {...toDefinitionProps(reference)} buttonChildren={children} />;
        case 'grape':
            return <GrapePopover {...toGrapeProps(reference)}>{children}</GrapePopover>;
        case 'wineStyle':
            return <WineStylePopover {...toWineStyleProps(reference)} buttonChildren={children} />;
    }
}

interface PopupProps {
    reference?: DefinitionPopupSchema | GrapePopupSchema | WineStylePopoverProps;
}

function toDefinitionProps(reference: DefinitionPopupSchema) {
    return {
        id: reference.slug || '',
        name: reference.name || '',
        summary: reference.summary || '',
        url: !reference.listPage?.path
            ? undefined
            : reference.hasText
              ? `${reference.listPage.path}#${reference.slug}`
              : reference.mainEntry?.hasText
                ? `${reference.listPage.path}#${reference.mainEntry?.slug}`
                : undefined,
    };
}

function toGrapeProps(reference: GrapePopupSchema) {
    return {
        id: reference.slug || '',
        name: reference.name || '',
        summary: reference.summary || '',
        grapeType: reference.grapeType,
        url: !reference.listPage?.path
            ? undefined
            : reference.hasText
              ? `${reference.listPage.path}#${reference.slug}`
              : reference.mainEntry?.hasText
                ? `${reference.listPage.path}#${reference.mainEntry?.slug}`
                : undefined,
    };
}

function toWineStyleProps(reference: WineStylePopoverProps) {
    return {
        id: reference.slug || '',
        name: reference.title || '',
        summary: reference.summary || '',
        image: reference.image,
        url: reference.page?.path ? `${reference.page.path}#${reference.slug}` : undefined,
    };
}
